import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import EmailModal from '@/components/General/EmailModal';
import SMSModal from '@/components/General/SMSModal';
import AddToRoleModal from '@/components/General/AddToRoleModal';
import { dropdownIcons } from '@/components/General/DropdownMenu';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import usePlatform from '@/utils/hooks/usePlatform';
import { addPeopleToRoles } from '@/data/roles';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule } from '@/utils/organizationEntitlements';

/**
 * Reusable hook for people-related batch actions (Send Email, Send SMS, Add to a Role).
 * Works with any table whose rows can be resolved to person IDs / person objects.
 *
 * @param {object} params
 * @param {(mode: string, ids: Set|Array) => Promise<string[]>} params.getSelectedPersonIds
 *        Async resolver: given the table's selection mode + ids, returns person IDs.
 * @param {(mode: string, ids: Set|Array) => Promise<object[]>} params.getSelectedPeople
 *        Async resolver: given the table's selection mode + ids, returns full person objects
 *        (must include `email` for Email, `Phones` for SMS, `id`/`firstName`/`lastName` for both).
 * @returns {{ peopleBatchActions: object[], modalElements: JSX.Element }}
 */
const usePeopleBatchActions = ({ getSelectedPersonIds, getSelectedPeople }) => {
    const queryClient = useQueryClient();
    const { isNative } = usePlatform();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();
    const canSendOrganizationSMS = !!loggedInUser?.can?.('Send SMS Messages');
    const canSendNativeIndividualSMS = isNative && !!loggedInUser?.canAll?.(['View People', 'View Contact Info']);

    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedPeopleForEmail, setSelectedPeopleForEmail] = useState(null);
    const [smsModalOpen, setSmsModalOpen] = useState(false);
    const [selectedPeopleForSMS, setSelectedPeopleForSMS] = useState(null);
    const [selectedPersonIdsForSMS, setSelectedPersonIdsForSMS] = useState([]);
    const [addToRoleModalOpen, setAddToRoleModalOpen] = useState(false);
    const [selectedPeopleCountForRole, setSelectedPeopleCountForRole] = useState(0);
    const [selectedPersonIdsForRole, setSelectedPersonIdsForRole] = useState([]);
    const [addToRoleLoading, setAddToRoleLoading] = useState(false);

    const peopleBatchActions = [
        ...(loggedInUser?.can?.('Send Mass Emails') ? [{
            name: 'Send Email',
            icon: { android: 'email', ios: 'envelope', web: 'mail-outline' },
            onSelect: async (mode, ids, _selectedCount) => {
                const selected = await getSelectedPeople(mode, ids);
                if (selected.length === 0) {
                    Toast.show({ type: 'error', text2: 'No linked people found for selected items.' });
                    return;
                }
                const emails = selected.map(p => p.email).filter(Boolean);
                if (emails.length === 0) {
                    Toast.show({ type: 'error', text2: 'No email addresses found for selected people.' });
                    return;
                }
                setSelectedPeopleForEmail(selected);
                setEmailModalOpen(true);
            },
        }] : []),
        ...(canSendOrganizationSMS || canSendNativeIndividualSMS ? [{
            name: 'Send SMS',
            icon: { android: 'sms', ios: 'message', web: 'chatbubble-outline' },
            onSelect: async (mode, ids, _selectedCount) => {
                const [selected, selectedIds] = await Promise.all([
                    getSelectedPeople(mode, ids),
                    getSelectedPersonIds(mode, ids),
                ]);
                if (selected.length === 0 || selectedIds.length === 0) {
                    Toast.show({ type: 'error', text2: 'No linked people found for selected items.' });
                    return;
                }
                setSelectedPeopleForSMS(selected);
                setSelectedPersonIdsForSMS(selectedIds);
                setSmsModalOpen(true);
            },
        }] : []),
        ...(canViewModule(entitlements, 'roles_dynamic') && (loggedInUser?.canAny?.(['Create Roles', 'Edit Roles']) || loggedInUser?.isAdmin) ? [{
            name: 'Add to a Role',
            icon: dropdownIcons.add,
            onSelect: async (mode, ids, _selectedCount) => {
                const selectedIds = await getSelectedPersonIds(mode, ids);
                if (selectedIds.length === 0) {
                    Toast.show({ type: 'error', text2: 'No linked people found for selected items.' });
                    return;
                }
                setSelectedPersonIdsForRole(selectedIds);
                setSelectedPeopleCountForRole(selectedIds.length);
                setAddToRoleModalOpen(true);
            },
        }] : []),
    ];

    const modalElements = (
        <>
            <EmailModal
                isOpen={emailModalOpen}
                onClose={() => { setEmailModalOpen(false); setSelectedPeopleForEmail(null); }}
                recipients={selectedPeopleForEmail || []}
            />
            <SMSModal
                isOpen={smsModalOpen}
                onClose={() => { setSmsModalOpen(false); setSelectedPeopleForSMS(null); setSelectedPersonIdsForSMS([]); }}
                recipients={selectedPeopleForSMS || []}
                recipientIDs={selectedPersonIdsForSMS}
            />
            {canViewModule(entitlements, 'roles_dynamic') && <AddToRoleModal
                isOpen={addToRoleModalOpen}
                onClose={() => { setAddToRoleModalOpen(false); setSelectedPersonIdsForRole([]); setSelectedPeopleCountForRole(0); }}
                selectedCount={selectedPeopleCountForRole}
                loading={addToRoleLoading}
                onConfirm={async (selectedRoles) => {
                    setAddToRoleLoading(true);
                    try {
                        const roleIDs = selectedRoles.map(r => r.id);
                        const result = await addPeopleToRoles(selectedPersonIdsForRole, roleIDs);
                        queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
                        queryClient.invalidateQueries({ queryKey: QueryKeys.roles.all() });
                        const skippedCount = result?.skipped ?? 0;
                        let message = `${selectedPeopleCountForRole} ${selectedPeopleCountForRole === 1 ? 'person' : 'people'} added to ${selectedRoles.length} ${selectedRoles.length === 1 ? 'role' : 'roles'}.`;
                        if (skippedCount > 0) message += ` ${skippedCount} ${skippedCount === 1 ? 'assignment' : 'assignments'} already existed.`;
                        Toast.show({ type: 'success', text2: message });
                        setAddToRoleModalOpen(false);
                        setSelectedPersonIdsForRole([]);
                        setSelectedPeopleCountForRole(0);
                    } catch {
                        Toast.show({ type: 'error', text2: 'Failed to add people to roles. Please try again.' });
                    } finally {
                        setAddToRoleLoading(false);
                    }
                }}
            />}
        </>
    );

    return { peopleBatchActions, modalElements };
};

export default usePeopleBatchActions;
